import React, { useMemo, useState, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { selectionToQueryParameter } from '@printdeal/product-code';
import queryString from 'query-string';
import cx from 'clsx';
import { useLocation } from '@gatsbyjs/reach-router';
import {
  Grid, Button, ButtonProps, Spinner,
} from '@printdeal/ui-library';
import { useAddLineItemMutation } from '@printdeal/checkout-frontend';
import { navigate } from 'gatsby';
import {
  SelectorContent,
  hasGroupedQuantity,
  useSelectorMachineContext,
  useSelectorMachineSend,
  useSelectorState,
  generateSelectionParameters,
} from '@printdeal/product-selector';
import { IAlternativeUpload } from '@printdeal/alternative-upload';
import { DELIVERY_TOOLS } from '@printdeal/business-logic';
import type { ExtendedCart } from '@printdeal/checkout-shared/';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { useGetExperiment } from '@printdeal/feature-management/react';
// @ts-expect-error
import { CartRequestDataBuilder } from '../../../../services/cart/CartRequestDataBuilder';
import FileDeliveryButton from '../../../file-delivery/FileDeliveryButton';
import { windowPresent } from '../../../../helpers/window';
import { dispatchDataLayerEntry } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import type { Locale } from '../../../../types/gatsby';
import messages from '../messages';
import useSystemPages from '../../../../hooks/useSystemPages';
import type { SelectedDeliveryTool } from '../../../../types/file-delivery/FileDelivery';
import ErrorAlert from '../../../error-handling/ErrorAlert';
import { buildLineItemsFromProduct } from '../../../../helpers/CheckoutHelpers';
import { IProductConfig, IProductPage, SelectionMode } from '../../../../types/product/productDetailPage';
import { useOrchestration } from '../../../orchestration/use-orchestration';
import { getDesignFromUrl } from '../../detail/helpers/designsHelper';
import {
  publishEventToSegmentForAddedToCart,
} from '../../../AnalyticsTracker/AnalyticsTrackerHelper';
import AnalyticsTrackerContext from '../../../AnalyticsTracker/AnalyticsTrackerContext';
import { getProductVersion } from '../../../../helpers/ProductHelper';

interface Props {
  selector: SelectorContent;
  attributes: SelectorContent['configuration']['attributes'];
  pageContent: IProductPage;
  selectedDeliveryTool: SelectedDeliveryTool | undefined;
  selectedServices?: string[];
}

const getEditorType = (editorType: string | string[] | null, isDirectUpload?: boolean): string | undefined => {
  if (isDirectUpload) {
    return 'direct-upload';
  }

  if (typeof editorType === 'string') {
    return editorType;
  }

  return undefined;
};

/**
 * When there is no artwork required, the add-to-cart button is primary
 * When in step-by-step mode, the file delivery button is secondary and the add-to-cart button is primary
 * When in default-values mode, the file delivery button is primary and the add-to-cart button is secondary
 * When on the review page, the file delivery button is secondary and the add-to-cart button is primary
 * */
const getButtonVariant = (
  button: 'add-to-cart' | 'file-delivery',
  selectionMode: SelectionMode,
  reviewPage: boolean,
  allowFileDelivery: boolean,
): ButtonProps['variant'] => {
  if (!allowFileDelivery) {
    return 'primary';
  }
  if (reviewPage) {
    return button === 'add-to-cart' ? 'primary' : 'secondary';
  }
  if (selectionMode === 'step-by-step') {
    return button === 'add-to-cart' ? 'primary' : 'secondary';
  }
  return button === 'add-to-cart' ? 'secondary' : 'primary';
};

const SubmitButtons = ({
  selector, attributes, pageContent, selectedDeliveryTool, selectedServices,
}: Props) => {
  const [
    alternativelyUploadedFiles,
    setAlternativelyUploadedFiles,
  ] = useState<IAlternativeUpload[] | undefined>(undefined);
  const [status, setStatus] = useState<'opening-editor' | 'idle'>('idle');
  const intl = useIntl();
  const location = useLocation();
  const orchestration = useOrchestration();
  const [analyticsTrackerData, dispatchToAnalyticsTracker] = useContext(AnalyticsTrackerContext);
  const selectorMachineSend = useSelectorMachineSend();
  const selectorState = useSelectorState();
  const isSelectionComplete = useSelectorMachineContext((state) => state.matches({ STARTED: 'SELECTION COMPLETE' }));
  const selectedPrice = useSelectorMachineContext((state) => state.context.price);
  const selectorSelection = useSelectorMachineContext((state) => state.context.selection);
  const totalQuantity = useSelectorMachineContext((state) => state.context.totalQuantity);
  const artworkRequired = useSelectorMachineContext((state) => state.context.artworkRequired);
  const groupSelection = useSelectorMachineContext((state) => state.context.group);
  const merchantRules = useSelectorMachineContext((state) => state.context.merchantRules);
  const selectionMode = useSelectorMachineContext((state) => state.context.selectionMode);
  const isReviewPage = useSelectorMachineContext((state) => state.context.isReviewPage);
  const selectedDeliveryOption = useSelectorMachineContext((state) => state.context.selectedDeliveryOption);
  const { deliveryTool } = merchantRules;
  // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
  const allowFileDelivery = (artworkRequired && Object.keys(deliveryTool).length > 0) || (deliveryTool?.checkmate === 'on' || deliveryTool?.editor === 'on');
  const isAlternativeUploadAllowed = selectionMode === 'default-values' && artworkRequired;
  const locale = intl.locale as Locale;
  const query = queryString.parse(location.search);
  const documentReferences = getDesignFromUrl(query);
  const documentReferenceUrl = documentReferences?.[0]?.documentReferenceUrl;
  const customizedProductId = typeof query.customizedProductId === 'string' ? query.customizedProductId : undefined;
  const getExperiment = useGetExperiment();
  const isExp1692 = getExperiment('exp1692_alternative_upload_file_check_pdp_v3', () => {}).experiment?.variation === 'variant';
  const editorType = alternativelyUploadedFiles ? 'alternative-upload' : query.editorType;
  const { reviewPath } = pageContent;
  const MACHINE_IDLE = selectorState === 'idle' || isSelectionComplete;

  const getDesignRedirectUrl = () => {
    if (!windowPresent) return '';
    const selectionQueryParameter = selectionToQueryParameter(selectorSelection);

    const group = groupSelection?.filter((entry) => entry.quantity > 0).map((entry) => ({
      ...entry.attributes,
      quantity: entry.quantity,
    }));

    const url = generateSelectionParameters(
      {
        selectionParameter: selectionQueryParameter,
        comboSelectionParameter: JSON.stringify(group),
        deliveryOptionIdParameter: selectedDeliveryOption?.id,
        quantityParameter: String(totalQuantity),
      },
    );
    // "Checkmatemode: checkmate" and "checkmate: on" will be renamed when MRC is replaced by the business rules app
    const editor = selectedDeliveryTool === 'editor' ? deliveryTool.editormode : deliveryTool.checkmatemode;

    if (url && orchestration) {
      url.searchParams.set('orchestrationId', orchestration.id);
    }

    if (url && editor) {
      url.searchParams.set('editorType', editor);
    }

    return `${window.location.origin}${reviewPath[locale]}?${url.searchParams.toString()}`;
  };

  const redirectUrl = useMemo(() => getDesignRedirectUrl(), [
    selectorSelection,
    groupSelection,
    deliveryTool,
    JSON.stringify(reviewPath),
    locale,
    selectedDeliveryTool,
    orchestration,
    selectedDeliveryOption?.id,
    totalQuantity,
  ]);

  const productVersion = getProductVersion(selector.product);

  const handleNoGroupSelection = () => {
    dispatchDataLayerEntry(
      'selector',
      'warning',
      {
        msg: 'no quantity grid was filled',
        contentfulId: pageContent.contentful_id,
      },
    );
    selectorMachineSend({
      type: 'Toggle panel',
      openPanel: true,
      showMessage: true,
      panelName: 'groupedQuantity',
      message: messages.noQuantity,
      variant: 'warning',
    });
  };
  const isStepByStep = selectionMode === 'step-by-step';
  const [cartPage] = useSystemPages('cart');
  const [alternativeUpload] = useSystemPages('alternativeUpload');

  const getProductInfo = () => {
    const groupedSelection = hasGroupedQuantity(attributes) ? groupSelection : undefined;
    if (!MACHINE_IDLE || !selectedPrice) return [];
    const userSelection = { ...selectorSelection, Quantity: String(totalQuantity) };
    const productPage = {
      ...pageContent,
      parentProduct: {
        ...selector?.product,
        key: selector?.product?.key,
        version: selector?.product.productVersion,
      },
      variant: {
        ...pageContent?.variant,
        sku: selector?.configuration?.variantSku,
      },
    };
    const productConfig = {
      price: {
        taxedPrice: {
          totalNet: {
            centAmount: selectedPrice.totalNet.centAmount,
            currencyCode: 'EUR',
          },
          totalGross: {
            centAmount: selectedPrice.totalGross.centAmount,
            currencyCode: 'EUR',
          },
        },
      },
    } as IProductConfig;

    // TODO: move this to improved way: using addLineItemMutation
    const cartRequestBuilder = new CartRequestDataBuilder();
    return cartRequestBuilder.buildLineItemsFromProduct({
      contentfulPageId: productPage.contentful_id,
      productKey: productPage.parentProduct.key,
      productVersion,
      deliveryOptionId: selectedDeliveryOption?.id,
      variantSku: selector.configuration.variantSku,
      userSelection,
      productConfig,
      productPage,
      editorType: getEditorType(editorType),
      documentReferenceUrl,
      groupSelection: groupedSelection,
      alternativelyUploadedFiles,
      orchestrationId: orchestration?.id,
      services: selectedServices && selectedServices.length ? selectedServices : undefined,
    });
  };

  const addLineItemMutation = useAddLineItemMutation({
    onSuccess: (data: ExtendedCart) => {
      /**
       * Publishing 'Product Added (Added to Cart)' event to segment for the CDP/Personalization team
       * for the CT Recommendation engine to work properly
       */
      const productInfo = getProductInfo();
      publishEventToSegmentForAddedToCart(
        analyticsTrackerData,
        data,
        productInfo,
        dispatchToAnalyticsTracker,
      );

      /**
       * Navigating to Cart on Successful addtion of lineitem into the Cart
       */
      navigate(cartPage?.path);
    },
  });

  const { isPending } = addLineItemMutation;

  return (
    <Grid className={cx('pd-gap-4', { 'pd-flex pd-justify-end pd-text-right': isStepByStep })}>
      {allowFileDelivery && selectedDeliveryTool && selectedDeliveryTool !== 'direct-upload' && (
      <Grid.Item className={(isReviewPage || isStepByStep) ? 'pd-order-last' : 'pd-order-first'}>
        <FileDeliveryButton
          formId="fileUploadButton"
          deliveryToolSettings={deliveryTool}
          selectedDeliveryTool={selectedDeliveryTool}
          redirectUrl={redirectUrl}
          mcpSKU={selector.product.mcpSku}
          variantSku={selector.configuration.variantSku}
          mcpAttributes={JSON.stringify({ ...selectorSelection, ...groupSelection[0]?.attributes })}
          productKey={selector.product.key}
          productVersion={productVersion}
          price={selectedPrice}
          productName={selector.product.productName}
          documentReference={documentReferenceUrl}
          reviewPath={reviewPath[locale]}
          source="PDP"
          productInfo={getProductInfo()}
          alternativeUploadPath={alternativeUpload.path}
          defaultDesignName={selector.product.productName}
          customizedProductId={customizedProductId}
          setAltUploadFiles={isAlternativeUploadAllowed ? setAlternativelyUploadedFiles : undefined}
          setStatus={setStatus}
          selectedServiceKeys={selectedServices && selectedServices.length ? selectedServices : undefined}
          contentfulProductPageId={pageContent.contentful_id}
          orchestrationId={orchestration?.id}
          deliveryOptionId={selectedDeliveryOption?.id}
        >
          <Button
            className={cx({ 'pd-w-full': !isStepByStep })}
            form="fileUploadButton"
            type="submit"
            variant={getButtonVariant('file-delivery', selectionMode, isReviewPage, allowFileDelivery)}
            disabled={!MACHINE_IDLE}
            data-ft-element="fileUploadButton"
            iconAfter={status === 'opening-editor'
              ? (
                <Spinner
                  variant="primary"
                  screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
                />
              )
              : undefined}
          >
            {documentReferenceUrl && (
            <FormattedMessage id="selector.edit.design" defaultMessage="Edit design" />
            )}
            {!documentReferenceUrl && selectedDeliveryTool === 'editor' && (
            <FormattedMessage id="selector.open.editor" defaultMessage="Start with designing" />
            )}
            {!documentReferenceUrl && selectedDeliveryTool === DELIVERY_TOOLS.UPLOADER_TOOL && (
            <FormattedMessage id="selector.open.uploaderTool" defaultMessage="Start with delivering" />
            )}
          </Button>
        </FileDeliveryButton>
      </Grid.Item>
      )}
      <Grid.Item className={`${(isReviewPage || isStepByStep) ? 'pd-order-first' : 'pd-order-last'}`}>
        <form
          id="add-product-to-cart"
          onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
            const groupedSelection = hasGroupedQuantity(attributes) ? groupSelection : undefined;
            if (!MACHINE_IDLE || !selectedPrice) return;
            event.preventDefault();
            if (groupedSelection && !groupedSelection.length) {
              handleNoGroupSelection();
              return;
            }

            if (alternativelyUploadedFiles) {
              addDataLayerEntry('alternative_upload', { action: 'add_to_cart_with_uploaded_files' });
            }

            const userSelection = { ...selectorSelection, Quantity: String(totalQuantity) };
            const productPage = {
              ...pageContent,
              parentProduct: {
                ...selector?.product,
                key: selector?.product?.key,
              },
              variant: {
                ...pageContent?.variant,
                sku: selector?.configuration?.variantSku,
              },
            };

            // exp1692_alternative_upload_file_check_pdp
            const isDirectUpload = selectedDeliveryTool === 'direct-upload' && isExp1692;
            const payload = buildLineItemsFromProduct({
              contentfulPageId: productPage.contentful_id,
              productKey: productPage.parentProduct.key,
              productVersion,
              variantSku: selector.configuration.variantSku,
              userSelection,
              editorType: getEditorType(editorType, isDirectUpload),
              documentReferences,
              groupSelection: groupedSelection,
              alternativelyUploadedFiles,
              orchestrationId: orchestration?.id,
              deliveryOptionId: selectedDeliveryOption?.id,
              ...selectedServices && selectedServices.length > 0 && {
                services: selectedServices,
              },
            });
            addLineItemMutation.mutate({
              ...payload,
              dataLayer: {
                productPage: {
                  productName: productPage.productName,
                  productNameOriginal: productPage?.productNameOriginal || productPage.productName,
                  contentful_id: productPage.contentful_id,
                  variant: {
                    sku: selector.configuration.variantSku,
                  },
                  parentProduct: {
                    key: selector.product.key,
                    mcpSku: selector.product.mcpSku,
                    productName: selector.product.productName,
                    productNameOriginal: selector.product.productNameOriginal || selector.product.productName,
                  },
                },
                price: {
                  centAmount: selectedPrice.totalNet.centAmount,
                  currencyCode: selectedPrice.totalNet.currencyCode,
                },
              },
            });
          }}
        >
          {addLineItemMutation.isError && (
          <ErrorAlert showDebugId={false}>
            <FormattedMessage
              id="product.errorDuringAddingProductToCart"
              defaultMessage="Problem with adding product to cart"
            />
          </ErrorAlert>
          )}
          <Button
            data-cy="add-to-cart-button"
            form="add-product-to-cart"
            type="submit"
            variant={getButtonVariant('add-to-cart', selectionMode, isReviewPage, allowFileDelivery)}
            className="pd-w-full"
            disabled={isPending || !MACHINE_IDLE}
            iconBefore={(isPending)
              ? (
                <Spinner
                  variant="primary"
                  screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
                />
              )
              : undefined}
          >
            <FormattedMessage id="product.button.addToCart" defaultMessage="Add to cart" />
          </Button>
        </form>
      </Grid.Item>
    </Grid>
  );
};

export default SubmitButtons;
